<template>
  <div class="weighted-spectra-container">
    <div class="weighted-spectra-header-container">
      <h1>Weighted 1/3rd Octave Spectra</h1>
      <div class="controls-container">
        <label for="weight-select">Select Weighting:</label>
        <select
          id="weight-select"
          v-model="selectedWeight"
          @change="updateChart"
        >
          <option value="A">A</option>
          <option value="C">C</option>
          <option value="Z">Z</option>
        </select>
      </div>
    </div>

    <div class="spectra-chart-container">
      <v-chart :option="chartOption" class="chart-container" autoresize />
    </div>
  </div>
</template>

<script>
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BoxplotChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from "echarts/components";
import { bankersRound } from "../helpers";
use([
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
  BoxplotChart,
  CanvasRenderer,
]);

export default {
  name: "WeightedSpectra",
  components: {
    VChart,
  },
  props: {
    APIparams: {
      type: Object,
      required: true,
    },
    weightedSpectra: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedWeight: "A",
      chartOption: {},
    };
  },
  methods: {
    updateChart() {
      const spectraData =
        this.weightedSpectra &&
        this.weightedSpectra[this.selectedWeight] &&
        Object.keys(this.weightedSpectra[this.selectedWeight]).length
          ? this.weightedSpectra[this.selectedWeight]
          : this.backendResponse.third_octaves || {};

      // Get the frequency bands (e.g., "100Hz", "125Hz", etc.).
      const bands = Object.keys(spectraData);

      let xTimestamps = this.APIparams.selected_times;
      if (
        xTimestamps === undefined ||
        xTimestamps === null ||
        xTimestamps.length === 0
      ) {
        const firstKey = Object.keys(spectraData)[0];
        xTimestamps = Object.keys(spectraData[firstKey]);
      }
      // For each band, iterate over xTimestamps, collect samples and apply the dB conversion.
      const source = [];
      bands.forEach((band) => {
        const bandData = spectraData[band] || {};
        const row = [];
        xTimestamps.forEach((ts) => {
          if (
            Object.prototype.hasOwnProperty.call(bandData, ts) &&
            bandData[`${ts}`] !== null &&
            bandData[`${ts}`] !== undefined
          ) {
            row.push(bankersRound(Number(bandData[ts])));
          }
        });
        source.push(row);
      });

      // Determine the maximum number of samples among all bands.
      let maxSamples = 0;
      source.forEach((row) => {
        if (row.length > maxSamples) {
          maxSamples = row.length;
        }
      });
      // Pad each row with nulls if needed.
      source.forEach((row) => {
        while (row.length < maxSamples) {
          row.push(null);
        }
      });

      // Set the chart option using the dataset and the boxplot transform.
      this.chartOption = {
        //   title: {
        //     text: `${this.selectedWeight} Weighted 1/3rd Octave Spectra Boxplot`
        //   },
        tooltip: {
          trigger: "item",
          axisPointer: {
            type: "shadow",
          },
          formatter: function (param) {
            // The transformed data from the boxplot transform is a five-number array.
            if (param.value instanceof Array && param.value.length === 6) {
              return [
                `Band: ${param.value[0]}`,
                `Min: ${bankersRound(param.value[1])}`,
                `Q1: ${bankersRound(param.value[2])}`,
                `Median: ${bankersRound(param.value[3])}`,
                `Q3: ${bankersRound(param.value[4])}`,
                `Max: ${bankersRound(param.value[5])}`,
              ].join("<br/>");
            }
            if (param.seriesType === "scatter") {
              return `${param.value[1]} `;
            }
            return param.value;
          },
        },
        dataset: [
          {
            // Raw data: [freq, data samples]
            source: source,
          },
          {
            // Transform the raw data to boxplot statistics.
            transform: {
              type: "boxplot",
              config: {
                itemNameFormatter: function (value) {
                  return `${bands[value.value]}`;
                },
              },
            },
          },
          {
            fromDatasetIndex: 1,
            fromTransformResult: 1,
          },
        ],
        xAxis: {
          type: "category",
          boundaryGap: true,
          name: "1/3rd Octave Spectra Band",
          nameGap: 30,
        },
        yAxis: {
          type: "value",
          name: "dB",
          min: 0,   // We pass a pre-filtered spectra to this so any value < 0 => 0 as the negative dB values are negligible for noise source contribution.
        },
        grid: {
          left: 30,
          top: 10,
          right: 20,
          bottom: 20,
        },
        series: [
          {
            name: "boxplot",
            type: "boxplot",
            datasetIndex: 1,
            itemStyle: {
              color: "#FFCB05",
              borderColor: "#009B69",
            },
          },
          {
            name: "outlier",
            type: "scatter",
            datasetIndex: 2,
            itemStyle: {
              color: "#B2D235",
            },
          },
        ],
      };
    },
  },
  mounted() {
    this.updateChart();
  },
};
</script>

<style scoped>
.weighted-spectra-container {
  padding: 10px;
  border: 2px solid var(--colour-border-strong);
  border-radius: 15px;
  background-color: var(--colour-bg-light);
  height: 100%;
  width: 100%;
}
.weighted-spectra-header-container {
  height: 80px;
}
.controls-container {
  margin-bottom: 10px;
}
.spectra-chart-container {
  width: 100%;
  height: calc(100% - 100px);
}
.chart-container {
  width: 100%;
  height: 100%;
}
.loading {
  text-align: center;
  margin-top: 10px;
}
</style>
